import React, {useState} from 'react';

import {Button, Icon, IconButton, Menu, Text} from '@albert-io/atomic';
import type {IconProp, IconStyle} from '@albert-io/atomic/atoms/Icon/Icon.react';

import {history} from 'client/history';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';
import {buildReportURLFromLevels} from 'client/AdaptiveSubjects/utils/buildReportUrlFromLevels';
import {
  ClassworkRow,
  classworkListClassName
} from 'client/TeacherDashboard/components/ClassworkRow';
import {ErrorRetryState} from 'client/TeacherDashboard/components/ErrorRetryState';
import {
  SkillPathListGroup,
  SkillPathListGroupEmpty
} from 'client/TeacherDashboard/SkillPaths/SkillPathListGroup';
import {formatDateRange, formatDateTime, getDueCopy} from 'client/TeacherDashboard/rowDateCopy';
import {AdaptiveOnboardingEmptyState} from 'client/TeacherDashboard/components/AdaptiveEmptyState';
import {
  noneCopy,
  statusLabel as toStatusLabel
} from 'client/TeacherDashboard/SkillPaths/SkillPathsEmptyState';
import {scopedDateOverride} from 'client/TeacherDashboard/SkillPaths/rowDateScope';
import type {
  SkillPathRowStatus,
  SkillPathStatusSegment
} from 'client/TeacherDashboard/SkillPaths/types';
import {DiagnosticStatus} from 'gql/types';

import {DeleteDiagnosticModal} from '../DeleteDiagnosticModal';
import {copyStudentDiagnosticLink} from '../studentDiagnosticLink';

const ROW_MENU_POPPER_PROPS = {
  modifiers: [
    {name: 'flip', options: {padding: 8}},
    {name: 'preventOverflow', options: {padding: 8}}
  ]
};

import type {DiagnosticListFieldsFragment} from './queries/Diagnostics.queries.generated';

const STATUS_SEGMENT: Record<DiagnosticStatus, SkillPathRowStatus> = {
  [DiagnosticStatus.Open]: 'open',
  [DiagnosticStatus.Closed]: 'closed',
  [DiagnosticStatus.Scheduled]: 'scheduled'
};

/**
 * A row's own status, which decides its card treatment and which date pair it renders. Falls back
 * rather than indexing blind: a status this bundle predates would otherwise be `undefined` and
 * throw on the `STATUS_DISPLAY` lookup, taking the whole tab down. On All, which names no status
 * of its own, `open` stands in.
 *
 * Not shared with the skill-path list's equivalent: this query selects the status on every tab, so
 * this reads the server field first where that one reads the segment first.
 */
const toRowStatus = (
  diagnostic: DiagnosticListFieldsFragment,
  segment: SkillPathStatusSegment
): SkillPathRowStatus =>
  (diagnostic.status && STATUS_SEGMENT[diagnostic.status]) ??
  (segment === 'all' ? 'open' : segment);

const STATUS_DISPLAY: Record<
  SkillPathRowStatus,
  {color: 'tertiary' | 'warning'; icon?: IconProp; iconStyle?: IconStyle}
> = {
  open: {color: 'tertiary'},
  scheduled: {color: 'warning', icon: 'clock', iconStyle: 'light'},
  closed: {color: 'tertiary', icon: 'ban', iconStyle: 'regular'}
};

const completionCopy = ({
  countOfCompletedStudents,
  countOfIncompleteStudents
}: DiagnosticListFieldsFragment): string => {
  const total = countOfCompletedStudents + countOfIncompleteStudents;
  return `${countOfCompletedStudents} / ${total} completed`;
};

const getSubtitleSections = (
  diagnostic: DiagnosticListFieldsFragment,
  status: SkillPathRowStatus,
  showExactDueDate: boolean,
  shortMonth: boolean
): string[] => {
  if (status === 'scheduled') {
    return [
      diagnostic.minStartDate
        ? `Starts ${formatDateRange(diagnostic.minStartDate, diagnostic.maxStartDate, formatDateTime)}`
        : 'Scheduled',
      completionCopy(diagnostic)
    ];
  }
  const dueCopy = getDueCopy(
    {minDueDate: diagnostic.minDueDate, maxDueDate: diagnostic.maxDueDate},
    showExactDueDate,
    shortMonth
  );
  if (status === 'closed') {
    return dueCopy
      ? ['Closed', dueCopy, completionCopy(diagnostic)]
      : ['Closed', completionCopy(diagnostic)];
  }
  return [dueCopy || 'Open', completionCopy(diagnostic)];
};

const editPath = (diagnostic: DiagnosticListFieldsFragment): string =>
  `/diagnostics/${diagnostic.diagnosticTemplateId}/assign/${diagnostic.id}`;

const DiagnosticRow = ({
  diagnostic,
  segment,
  classroomId,
  onDeleted,
  isRowRedesignEnabled
}: {
  diagnostic: DiagnosticListFieldsFragment;
  segment: SkillPathStatusSegment;
  classroomId?: string;
  onDeleted: () => void;
  isRowRedesignEnabled: boolean;
}) => {
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const status = toRowStatus(diagnostic, segment);
  const row = {...diagnostic, ...scopedDateOverride(diagnostic, segment, status)};
  const {color, icon, iconStyle} = STATUS_DISPLAY[status];
  const goToEdit = () => history.push(editPath(diagnostic));
  const actionSize = isRowRedesignEnabled ? 'm' : 's';
  // Report URLs can't express the lowest level, so this lands on Subjects & Topics and lets
  // the diagnostic filter's adaptive mode drop it to skills.
  const goToReport = () =>
    history.push(
      `${buildReportURLFromLevels(
        [],
        // Read at click time, so no bridge is needed: this runs outside render.
        masqueradeStore.getUserIdByMasqueradeState(),
        classroomId
      )}/subjects?diagnostic=${diagnostic.id}`
    );

  return (
    <>
      <ClassworkRow
        actions={
          <>
            <IconButton
              icon='chart-line'
              iconStyle='regular'
              label={`View Report for ${diagnostic.name}`}
              tooltip='View Report'
              size={actionSize}
              onClick={goToReport}
            />
            <IconButton
              icon='pencil'
              iconStyle='regular'
              label={`Edit ${diagnostic.name}`}
              tooltip='Edit Diagnostic'
              size={actionSize}
              onClick={goToEdit}
            />
            <Menu position='bottom-end' closeOnItemClick popperProps={ROW_MENU_POPPER_PROPS}>
              <Menu.Trigger>
                {(triggerProps) => (
                  <IconButton
                    {...triggerProps}
                    icon='ellipsis'
                    iconStyle='regular'
                    label={
                      isRowRedesignEnabled ? `Actions for ${diagnostic.name}` : 'Diagnostic options'
                    }
                    tooltip={isRowRedesignEnabled ? 'Actions' : undefined}
                    size={actionSize}
                    variant={isRowRedesignEnabled ? 'outlined' : 'text'}
                    data-testid='diagnostic-row-options-btn'
                  />
                )}
              </Menu.Trigger>
              <Menu.Content>
                <Menu.Item
                  leftIconProps={{icon: 'cog', className: 'o-menu__item-icon opacity-100!'}}
                  onClick={goToEdit}
                  data-testid='diagnostic-row-edit-btn'
                >
                  Edit Recipient Settings
                </Menu.Item>
                <Menu.Item
                  leftIconProps={{icon: 'chart-line', className: 'o-menu__item-icon opacity-100!'}}
                  onClick={goToReport}
                  data-testid='diagnostic-row-report-btn'
                >
                  View Report
                </Menu.Item>
                <Menu.Item
                  leftIconProps={{icon: 'copy', className: 'o-menu__item-icon opacity-100!'}}
                  onClick={() => copyStudentDiagnosticLink(diagnostic.id)}
                  data-testid='diagnostic-row-share-link-btn'
                >
                  Student Share Link
                </Menu.Item>
                <Menu.Item
                  leftIconProps={{
                    icon: 'trash-alt',
                    color: 'negative',
                    className: 'o-menu__item-icon opacity-100!'
                  }}
                  textProps={{color: 'negative'}}
                  onClick={() => setIsConfirmingDelete(true)}
                  data-testid='diagnostic-row-delete-btn'
                >
                  Delete
                </Menu.Item>
              </Menu.Content>
            </Menu>
          </>
        }
        data-testid='diagnostic-row'
        icon={['fal', 'file-contract']}
        iconStyle='light'
        renderSubtitle={(isHovered) => (
          <Text as='div' className='truncate' color={color} size='s'>
            {icon ? (
              <Icon
                className='mr-x0-half text-xs!'
                color={color}
                icon={icon}
                iconStyle={iconStyle}
              />
            ) : null}
            {getSubtitleSections(row, status, isHovered, isRowRedesignEnabled).map(
              (part, index) => (
                <React.Fragment key={part}>
                  {index > 0 && <span className='mx-x1'>•</span>}
                  {part}
                </React.Fragment>
              )
            )}
          </Text>
        )}
        title={
          <Text className='content-primary truncate' size='m'>
            {diagnostic.name}
          </Text>
        }
        isRowRedesignEnabled={isRowRedesignEnabled}
        onOpen={goToEdit}
      />
      {isConfirmingDelete && (
        <DeleteDiagnosticModal
          diagnosticId={diagnostic.id}
          onClose={() => setIsConfirmingDelete(false)}
          onDeleted={onDeleted}
        />
      )}
    </>
  );
};

interface Props {
  diagnostics: DiagnosticListFieldsFragment[];
  /** Total matching the status + filters, shown beside the group title. */
  total: number;
  segment: SkillPathStatusSegment;
  /** Every status is empty under the current filters — show the pitch, not "no open …". */
  isEmptyInEveryStatus: boolean;
  /** More rows match than are loaded; the button both fetches and reveals them. */
  hasMore: boolean;
  isLoadingMore: boolean;
  onShowMore: () => void;
  classroomId?: string;
  hasError: boolean;
  onRetry: () => void;
  /** Re-reads the list and the status counts, which a deleted row leaves stale. */
  onDeleted: () => void;
  isRowRedesignEnabled?: boolean;
}

export const DiagnosticsGroup = ({
  diagnostics,
  total,
  segment,
  isEmptyInEveryStatus,
  hasMore,
  isLoadingMore,
  onShowMore,
  classroomId,
  hasError,
  onRetry,
  onDeleted,
  isRowRedesignEnabled = false
}: Props) => {
  const statusLabel = toStatusLabel(segment).toLowerCase();
  const remaining = total - diagnostics.length;

  return (
    <SkillPathListGroup count={hasError ? undefined : total} title='Diagnostics'>
      {hasError ? (
        <ErrorRetryState copy='We couldn’t load your diagnostics.' onRetry={onRetry} />
      ) : diagnostics.length === 0 ? (
        <SkillPathListGroupEmpty inset={isEmptyInEveryStatus ? 'panel' : 'text'}>
          {isEmptyInEveryStatus ? (
            <AdaptiveOnboardingEmptyState
              classroomId={classroomId}
              layout='inline'
              variant='diagnostics'
            />
          ) : (
            <Text color='secondary'>{noneCopy(statusLabel, 'diagnostics')}</Text>
          )}
        </SkillPathListGroupEmpty>
      ) : (
        <>
          <ul className={classworkListClassName(isRowRedesignEnabled)}>
            {diagnostics.map((diagnostic) => (
              <li key={diagnostic.id}>
                <DiagnosticRow
                  classroomId={classroomId}
                  diagnostic={diagnostic}
                  isRowRedesignEnabled={isRowRedesignEnabled}
                  segment={segment}
                  onDeleted={onDeleted}
                />
              </li>
            ))}
          </ul>
          {hasMore && (
            <div className='pl-x8'>
              <Button
                color='secondary'
                disabled={isLoadingMore}
                roundness='pill'
                size='s'
                variant='outlined'
                onClick={onShowMore}
              >
                {isLoadingMore ? 'Loading…' : `Show ${remaining} More`}
              </Button>
            </div>
          )}
        </>
      )}
    </SkillPathListGroup>
  );
};
